import {IReg} from "/@lib@/commons/registry.js";
import {JSX} from "/@lib@/commons/xml/dom.js";
import {LOCALE} from "/@lib@/commons/lang.js";
import {OButtonInit} from "/@back@/commons/widgets/buttons.js";

export interface IHelpDb {

	/**
	 * A appeler pour charger la HelpDb. Si traitement async potentiellement long, retourner une promise
	 * permettant de notifier l'utilisateur du chargement en cours, sinon retourner this.
	 */
	initIfNeeded(): this | Promise<this>

	getFragment(id: helpId | null): Promise<IHelpFrag | undefined>

	search(crit: OHelpSearchExp): Promise<IHelpSearchResults>

	getIndex(key: string): Promise<IHelpIndex | undefined>

	getKeyIndexes(role: EHelpIndexRole): Promise<Set<string>>

	getIndexesFor(frag: IHelpFrag): Promise<(IHelpIndex | IHelpIndexNode)[]>

	getIndexesWithRoleFor(frag: IHelpFrag, indexRole: EHelpIndexRole): Promise<(IHelpIndex | IHelpIndexNode)[]>
}


export type helpId = string

/**
 * Fragment de contenu.
 * Un fragment titré est un topic.
 * Un fragment non titré (donc n'est pas un topic) est référençable dans un contexte précis,
 * comme un tooltip ou la mutualisation d'un fragment entre plusieurs pages.
 */
export interface IHelpFrag {

	readonly fragId: helpId

	readonly cls?: string

	hasBody(): boolean

	makeBody(options: IHelpPageTplContext): Promise<HTMLElement> | null

	isTopic(): this is IHelpTopic
}

/** Contenu autonome **titré** repérable dans un outline. */
export interface IHelpTopic extends IHelpFrag {
	titleString: string

	makeTitle(options: IHelpPageTplContext): HTMLElement
}

/** Index d'une HelpDb */
export interface IHelpIndex {
	/** Nom de l'index */
	indexKey: string

	/** Role de cet index */
	indexRole: EHelpIndexRole

	/**
	 * Membres de l'index
	 * @param deepFetch Garanti la présence de IHelpIndexNode.members en accès synchrone.
	 */
	getMembers(deepFetch?: boolean): Promise<(IHelpFrag | IHelpIndexNode)[]>

	/** Est un IHelpIndexNode */
	isTreeNode(): this is IHelpIndexNode
}

export type EHelpIndexRole =
/** Définit la structure d'un schéma xml */
	"structXml"
	/** Définit le pland d'un contenu édotorial */
	| "outline"
	/** Permet de reconstruire une page à partir d'un de ces fragments */
	| "page"


/** Noeud dans un index de type arbre de la HelpDb */
export interface IHelpIndexNode extends IHelpIndex {
	/** Fragment représentant ce noeud */
	frag: IHelpFrag
	/** Noeud parent */
	parent: IHelpIndexNode | null

	/**
	 * Accès synchrone aux membres si l'impl el permet ou si ce IHelpIndexNode a été obtenu
	 * via un IHelpIndex.getMembers(deepFetch=true).
	 */
	members?: (IHelpFrag | IHelpIndexNode)[]
}


/** Contexte pour la construction des pages */
export interface IHelpPageTplContext {
	/** Registre du contexte applicatif courant*/
	reg: IReg<unknown>

	/** HelpDb globale (union des helpDb pour résolutions des liens inter-db). */
	helpDb: IHelpDb

	/** Fonction pour consulter une autre page à partir d'un id de fragment */
	openFragId(fragId: string, ev?: Event): Promise<void>

	/** Fonction pour consulter une autre page */
	openFrag(frag: IHelpFrag, ev?: Event): Promise<void>

	gotoNext?(): void

	gotoPrev?(): void
}

/** Expression de recherche dans la HelpDb */
export interface OHelpSearchExp {
	/** Texte de recherche */
	text: string
	/** Liste restrictive des indexKey dans lesquels chercher les fragments */
	inScopes?: string[]
	/** Nombre max d'entrées retournées */
	max?: number
	//returnTopics?: boolean; si frag non titré trouvé, utilise les index pour remonter aux Topics titrés
	//makeExtract?: boolean
}

/** Résultats d'une recherche dans la HelpDb */
export interface IHelpSearchResults extends Array<IHelpSearchResult> {
}

export interface IHelpSearchResult {
	found: IHelpFrag
	score?: number
	extract?: HTMLElement
}


/** */
export type IHelpPageTpl = (frag: IHelpFrag, options: IHelpPageTplContext) => Promise<HTMLElement>

export namespace HELP {

	export const UNIONDB_SEP_ID = "_";

	export const simpleHelpPageTpl: IHelpPageTpl = async (frag: IHelpFrag, helpCtx: IHelpPageTplContext) => {
		return <div class={frag.cls ? "page " + frag.cls : "page tag"}>
			{buildTitle(frag, helpCtx)}
			{await buildBody(frag, helpCtx)}
		</div>
	}

	export const withCallersHelpPageTpl: IHelpPageTpl = async (frag: IHelpFrag, helpCtx: IHelpPageTplContext) => {
		return <div class={frag.cls ? "page " + frag.cls : "page tag"}>
			{buildHead(frag, helpCtx)}
			{await buildCallers(frag, helpCtx)}
			{buildTitle(frag, helpCtx)}
			{await buildBody(frag, helpCtx)}
		</div>
	}

	export const helpClsLabels: Dict<string> = {
		"tag": "Balise",
		"it": "Item type",
		"concept": "Concept",
		"chapter": "Chapitre"
	}

	export function buildHead(frag: IHelpFrag, helpCtx: IHelpPageTplContext): HTMLElement | null {
		return <div class={frag.cls ? "head " + frag.cls : "head tag"}>
			{helpCtx.gotoPrev ? <c-button î={{title: "Précédent", uiContext: "bar", icon: "/@skin@/help/previous.svg"} satisfies OButtonInit} onclick={() => helpCtx.gotoPrev()}/> : undefined}
			{helpCtx.gotoNext ? <c-button î={{title: "Suivant", uiContext: "bar", icon: "/@skin@/help/next.svg"} satisfies OButtonInit} onclick={() => helpCtx.gotoNext()}/> : undefined}
			<span style="flex:1"/>
			<span class="headTi">{helpClsLabels[frag.cls || "tag"]}</span>
		</div>
	}

	export function buildTitle(frag: IHelpFrag, helpCtx: IHelpPageTplContext): HTMLElement | null {
		if (frag.isTopic()) return <h2>{frag.makeTitle(helpCtx)}</h2>;
	}

	export function buildBody(frag: IHelpFrag, helpCtx: IHelpPageTplContext): Promise<HTMLElement> | null {
		if (frag.hasBody()) return frag.makeBody(helpCtx);
	}

	export async function buildCallers(frag: IHelpFrag, helpCtx: IHelpPageTplContext): Promise<HTMLElement | undefined> {
		if (frag.cls === "tag") {
			const parents = await helpCtx.helpDb.getIndexesWithRoleFor(frag, "structXml");
			if (!parents || parents.length === 0) return null;
			if (parents.length > 12) {
				//Balise très générique genre @code, sc:refUri...
				const countContexts = parents.length;
				//Lister au moins les items Types racines ?
				return <div class="xmlHier">
					<c-msg level="info">Balise exploitée dans {countContexts} contextes (non affichés)</c-msg>
				</div>;
			}
			const stacks: IHelpTopic[][] = [];
			for (const idxNode of parents) {
				if (idxNode.isTreeNode()) {
					const stack: IHelpTopic[] = [];
					stacks.push(stack);
					for (let n = idxNode; n; n = n.parent) {
						stack.push(n.frag as IHelpTopic);
					}
					stack.reverse();
				}
			}
			stacks.sort((f1, f2) => LOCALE.SORT_CMP.compare(f1[0].titleString, f2[0].titleString));
			const r = <div class="xmlHier"/>;
			_buildXmlEntry(stacks, r, helpCtx);
			return r;
		} else {
			const parents = await helpCtx.helpDb.getIndexesWithRoleFor(frag, "outline");
			if (!parents || parents.length === 0) return null;
			const r = <div class="stack"/>;
			for (const parent of parents) {
				if (parent.isTreeNode()) {
					let p: IHelpIndexNode = parent;
					let ctn: HTMLElement;
					while (p) {
						const frag = p.frag;
						ctn = <div class="stackEntry">
							<a onclick={(ev: Event) => helpCtx.openFrag(frag, ev)}>{(frag as IHelpTopic).makeTitle(helpCtx)}</a>
							{ctn}
						</div>
						p = p.parent;
					}
					r.appendChild(<div class="stackLine">{ctn}</div>);
				}
			}
			return r;
		}
	}

	function _buildXmlEntry(stacks: IHelpTopic[][], out: HTMLElement, helpCtx: IHelpPageTplContext) {
		for (let i = 0; i < stacks.length; i++) {
			const line = out.appendChild(<div class="xmlLine"/>);
			const stack = stacks[i];
			for (const frag of stack) {
				if (!frag) continue;
				if (line.hasChildNodes()) line.appendChild(<span> / </span>);
				line.appendChild(<a onclick={(ev: Event) => helpCtx.openFrag(frag, ev)}>{frag.makeTitle(helpCtx)}</a>);
			}
		}
	}
}
